import React, { useEffect, useRef, useState } from "react";
import { Download, Upload, CloudDownload, Database, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { D, JAKARTA, inputStyle, Chip } from "./ui";

const LABEL = { cities: "Kota", catalog: "Katalog bangunan", mayors: "Walikota", radio_tracks: "Lagu radio", radio_announce: "Pengumuman", koran: "Artikel koran", info_pages: "Halaman info", warga: "Warga", pesan_warga: "Pesan warga", likes: "Suka", comments: "Komentar", media: "Media" };
const card = { background: "#fff", borderRadius: 26, padding: "20px 22px", boxShadow: "0 8px 22px rgba(20,20,20,0.05)" };
const btn = (bg = D.dark, color = "#fff", extra = {}) => ({ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px", background: bg, color, border: 0, borderRadius: 99, fontSize: 13, fontWeight: 700, fontFamily: JAKARTA, cursor: "pointer", ...extra });

// Pengaturan → Data & Migrasi: isi database ini, tarik data dari server lain (preview → live), cadangan & pulihkan.
export function DataPanel({ token }) {
  const [ringkas, setRingkas] = useState(null);
  const muat = () => api.adminDataRingkasan(token).then(setRingkas).catch(() => {});
  useEffect(() => { muat(); }, []); // eslint-disable-line
  return (
    <div data-testid="data-panel" style={{ fontFamily: JAKARTA, display: "flex", flexDirection: "column", gap: 18 }}>
      <div>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: -0.5, color: D.ink }}>Data & Migrasi</h3>
        <p style={{ margin: "4px 0 0", fontSize: 12.5, color: D.sub }}>Pindahkan seluruh isi kota dari server preview ke versi live, atau simpan cadangan. Akun admin tidak pernah ikut disalin.</p>
      </div>
      <Ringkasan ringkas={ringkas} />
      <Tarik token={token} onDone={muat} />
      <Cadangan token={token} onDone={muat} />
    </div>
  );
}

function Ringkasan({ ringkas }) {
  const total = ringkas ? Object.values(ringkas.koleksi).reduce((a, b) => a + b, 0) : 0;
  return (
    <div data-testid="data-ringkasan" style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ width: 36, height: 36, borderRadius: 12, background: D.blueSoft, display: "grid", placeItems: "center" }}><Database size={16} color={D.blue} /></span>
        <div style={{ flex: 1 }}>
          <b style={{ fontSize: 14.5, color: D.ink }}>Isi database server ini</b>
          <span style={{ display: "block", fontSize: 12, color: D.sub }}>{ringkas ? `${ringkas.db} · ${total} dokumen` : "Memuat…"}</span>
        </div>
        {ringkas && (ringkas.koleksi.cities === 0 || ringkas.koleksi.catalog === 0) && <Chip tone="red" testid="data-kosong">Kota/katalog kosong</Chip>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
        {ringkas && Object.entries(ringkas.koleksi).map(([k, v]) => (
          <div key={k} data-testid={`data-count-${k}`} style={{ padding: "10px 12px", borderRadius: 14, background: v === 0 ? "#fee2e2" : D.cream, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: D.sub, fontWeight: 600 }}>{LABEL[k] || k}</span>
            <b style={{ fontSize: 14, color: v === 0 ? D.danger : D.ink }}>{v}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

function Tarik({ token, onDone }) {
  const [form, setForm] = useState({ source_url: "", email: "", password: "", include_media: true });
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    api.adminDataStatus(token).then(j => { setJob(j); if (j.state === "berjalan") mulaiPoll(); }).catch(() => {});
    return () => clearInterval(timer.current);
  }, []); // eslint-disable-line

  function mulaiPoll() {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const j = await api.adminDataStatus(token);
        setJob(j);
        if (j.state !== "berjalan") { clearInterval(timer.current); onDone(); }
      } catch { /* abaikan, coba lagi */ }
    }, 1500);
  }

  async function submit(e) {
    e.preventDefault();
    if (!window.confirm("Seluruh data kota di server INI akan DIGANTI dengan data dari sumber (akun admin tetap). Lanjutkan?")) return;
    setErr("");
    try {
      await api.adminDataTarik(token, form);
      setJob({ state: "berjalan", langkah: "Memulai…", log: [] });
      mulaiPoll();
    } catch (x) { setErr(x.message); }
  }

  const jalan = job?.state === "berjalan";
  return (
    <div data-testid="data-tarik" style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <span style={{ width: 36, height: 36, borderRadius: 12, background: D.blueSoft, display: "grid", placeItems: "center" }}><CloudDownload size={16} color={D.blue} /></span>
        <div>
          <b style={{ fontSize: 14.5, color: D.ink }}>Tarik data dari server lain</b>
          <span style={{ display: "block", fontSize: 12, color: D.sub }}>Mis. dari preview ke live: kota, katalog (termasuk lukisan AI), walikota, radio, koran, info, warga, pesan + berkas media.</span>
        </div>
      </div>
      <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 10, alignItems: "center", marginTop: 12 }}>
        <input data-testid="tarik-url" value={form.source_url} onChange={e => setForm({ ...form, source_url: e.target.value })} placeholder="URL sumber, mis. https://nama-app.preview.emergentagent.com" required disabled={jalan} style={inputStyle} />
        <input data-testid="tarik-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email admin sumber" type="email" required disabled={jalan} style={inputStyle} />
        <input data-testid="tarik-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Password admin sumber" type="password" required disabled={jalan} style={inputStyle} />
        <button data-testid="tarik-submit" type="submit" disabled={jalan} style={btn(D.blue, "#fff", { opacity: jalan ? .6 : 1 })}>
          {jalan ? <Loader2 size={15} style={{ animation: "spin 1s linear infinite" }} /> : <CloudDownload size={15} />} {jalan ? "Berjalan…" : "Tarik sekarang"}
        </button>
        <label style={{ gridColumn: "1 / -1", display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, color: D.ink, fontWeight: 600 }}>
          <input data-testid="tarik-media" type="checkbox" checked={form.include_media} onChange={e => setForm({ ...form, include_media: e.target.checked })} disabled={jalan} />
          Salin juga berkas media (MP3 radio, sampul koran) yang belum ada di object storage server ini
        </label>
      </form>
      {err && <p data-testid="tarik-error" style={{ margin: "8px 0 0", fontSize: 12.5, color: D.danger, fontWeight: 600 }}>{err}</p>}
      {job && job.log?.length > 0 && (
        <div data-testid="tarik-log" style={{ marginTop: 14, padding: "12px 14px", borderRadius: 16, background: "#141414", color: "#e8e6da", fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11.5, lineHeight: 1.7, maxHeight: 220, overflowY: "auto" }}>
          {job.log.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      )}
      {job?.state === "selesai" && <p data-testid="tarik-selesai" style={{ margin: "10px 0 0", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#189a5b", fontWeight: 700 }}><CheckCircle2 size={15} /> Selesai — buka halaman kota untuk memastikan bangunan sudah tampil.</p>}
      {job?.state === "gagal" && <p data-testid="tarik-gagal" style={{ margin: "10px 0 0", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: D.danger, fontWeight: 700 }}><AlertTriangle size={15} /> {job.error}</p>}
    </div>
  );
}

function Cadangan({ token, onDone }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  async function unduh() {
    setBusy(true); setMsg("");
    try {
      const snap = await api.adminDataEkspor(token);
      const blob = new Blob([JSON.stringify(snap)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `jawsel-cadangan-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      setMsg("Cadangan diunduh");
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  }

  async function pulihkan(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!window.confirm(`Pulihkan dari "${f.name}"? Seluruh data kota di server ini akan DIGANTI.`)) return;
    setBusy(true); setMsg("");
    try {
      const snap = JSON.parse(await f.text());
      const r = await api.adminDataImpor(token, snap);
      setMsg("Dipulihkan: " + Object.entries(r.hasil).map(([k, v]) => `${LABEL[k] || k} ${v}`).join(", "));
      onDone();
    } catch (x) { setMsg(x.message); }
    setBusy(false);
  }

  return (
    <div data-testid="data-cadangan" style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ width: 36, height: 36, borderRadius: 12, background: D.blueSoft, display: "grid", placeItems: "center" }}><Download size={16} color={D.blue} /></span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <b style={{ fontSize: 14.5, color: D.ink }}>Cadangan & pulihkan</b>
          <span style={{ display: "block", fontSize: 12, color: D.sub }}>Unduh seluruh data sebagai berkas .json; pulihkan kapan saja (berkas media tetap di object storage).</span>
        </div>
        <button data-testid="cadangan-unduh" onClick={unduh} disabled={busy} style={btn()}><Download size={15} /> Unduh cadangan</button>
        <button data-testid="cadangan-pulihkan" onClick={() => fileRef.current?.click()} disabled={busy} style={btn("#fff", D.ink, { border: `1.5px solid ${D.line}` })}><Upload size={15} /> Pulihkan dari berkas</button>
        <input ref={fileRef} data-testid="cadangan-file" type="file" accept="application/json,.json" onChange={pulihkan} style={{ display: "none" }} />
      </div>
      {msg && <p data-testid="cadangan-msg" style={{ margin: "10px 0 0", fontSize: 12.5, color: D.sub, fontWeight: 600 }}>{msg}</p>}
    </div>
  );
}
